import { useMsal } from '@azure/msal-react';
import { Button, Text } from '@fluentui/react-components';
import { Attach12Regular, Dismiss12Regular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { useRef, useState } from 'react';
import { useStores } from '../../../common/hooks';
import { emailTemplateAttachmentConfiguration } from '../../../configurations';
import { fileUploadValidator, getFileNameFromUrl } from '../../../shared/functions';
import { CustomMessageBar } from '../../shared-components';
import { FlexBox } from '../../shared-components/flex-box';
import './AttachmentsSection.scss';

const AttachmentsSectionImpl: React.FC = () => {
  const {
    instance
  } = useMsal();

  const {
    apiStore,
    createOrEditEmailTemplatePageUiStore,
  } = useStores();

  const uploadAttachmentRef = useRef<HTMLInputElement>(null);
  const [inputKeyForAttachment, setInputKeyForAttachment] = useState<string>('');
  const [fileErrorMessagesForAttachment, setFileErrorMessagesForAttachment] = useState<string>('');
  const [isShowAttachmentAlert, setIsShowAttachmentAlert] = useState<boolean>(false);

  const handleUploadedAttachmentFile = (files: FileList) => {
    inputKeyForAttachment === files[0].name ? setInputKeyForAttachment('') : setInputKeyForAttachment(files[0].name);
    if (!files || !files[0] || files.length > 1) return;

    const file = files[0];
    const validationResult = fileUploadValidator(file, emailTemplateAttachmentConfiguration);
    if (!validationResult.isValid) {
      setIsShowAttachmentAlert(true);
      setFileErrorMessagesForAttachment(`${file.name} - ${validationResult.errorMessages[0]}`);
      return;
    }

    createOrEditEmailTemplatePageUiStore.addAttachment(file);
  };

  const onClearAttachmentFile = (file: File) => {
    createOrEditEmailTemplatePageUiStore.removeAttachment(file);
  };

  const onClearExistingAttachmentFile = (emailTemplateAttachmentId: number) => {
    createOrEditEmailTemplatePageUiStore.removeExistingAttachment(emailTemplateAttachmentId);
    createOrEditEmailTemplatePageUiStore.setAttachmentTicketId(undefined);
  };

  const createAttachmentTicket = async (): Promise<void> => {
    try {
      const ticketId = await apiStore.createEmailResourcesDownloadTicket(instance, 'EmailAttachment');
      createOrEditEmailTemplatePageUiStore.setAttachmentTicketId(ticketId);
    } catch (error) {
      // TODO: Handle error
    }
  };

  const downloadAttachment = async (fileLocationUrl?: string): Promise<void> => {
    if (!fileLocationUrl) return;
    await createAttachmentTicket();
    var fileName = getFileNameFromUrl(fileLocationUrl);
    if (!createOrEditEmailTemplatePageUiStore.attachmentTicketId) return;
    const downloadUrl = `/api/email-resources/attachments/${fileName}?ticketId=${createOrEditEmailTemplatePageUiStore.attachmentTicketId}`;
    window.location.href = downloadUrl;
  };

  const handleCloseAttachmentAlert = () => {
    setIsShowAttachmentAlert(false);
    setFileErrorMessagesForAttachment('');
  };

  return (
    <div
      className='attachments-section'
    >
      <Text
        weight='semibold'
      >
        Attachments
      </Text>
      <div>
        {
          fileErrorMessagesForAttachment.length > 0 && (
            <>
              {
                isShowAttachmentAlert && (
                  <CustomMessageBar
                    message={fileErrorMessagesForAttachment}
                    notificationTypeCode='warning'
                    showDismissButton
                    onDismissClicked={() => handleCloseAttachmentAlert()}
                  />
                )}
            </>
          )
        }
      </div>
      <div>
        {
          createOrEditEmailTemplatePageUiStore.emailTemplateAttachments && (
            <>
              {
                createOrEditEmailTemplatePageUiStore.emailTemplateAttachments.map((attachment) => {
                  return (
                    <FlexBox
                      key={attachment.emailTemplateAttachmentId}
                      aligns='center'
                      className='attachments-section__uploaded-file'
                    >
                      <div
                        className='attachment-name'
                      >
                        <button
                          onClick={() => downloadAttachment(attachment.fileLocationUrl)}
                        >
                          <Attach12Regular />
                          <span>{attachment.fileName}</span>
                        </button>
                      </div>
                      <Button
                        title='delete'
                        size='small'
                        onClick={() => onClearExistingAttachmentFile(attachment.emailTemplateAttachmentId!)}
                        icon={<Dismiss12Regular />}
                      />
                    </FlexBox>
                  );
                })
              }
            </>
          )
        }
        {
          createOrEditEmailTemplatePageUiStore.selectedAttachments.map((imageFile, index) => (
            <FlexBox
              key={index}
              aligns='center'
              className='attachments-section__uploaded-file'
            >
              <div
                className='attachment-name'
              >
                <Attach12Regular />
                <span>{imageFile.name}</span>
              </div>
              <Button
                title='delete'
                size='small'
                onClick={() => onClearAttachmentFile(imageFile)}
                icon={<Dismiss12Regular />}
              />
            </FlexBox>
          ))
        }
      </div>
      <div>
        <Button
          appearance='transparent'
          className='attach-btn'
          onClick={() => uploadAttachmentRef.current?.click()}
        >
          Add attachment +
        </Button>
        <input
          key={inputKeyForAttachment}
          type='file'
          ref={uploadAttachmentRef}
          onChange={(event) => handleUploadedAttachmentFile(event.currentTarget.files as FileList)}
          accept={emailTemplateAttachmentConfiguration.supportedFileExtensions.join(',')}
          hidden
        />
      </div>
      {
        (createOrEditEmailTemplatePageUiStore.isBeingValidated && createOrEditEmailTemplatePageUiStore.attachmentTotalSizeValidationMessage) &&
        <Text
          className='text--red'
          size={200}
        >
          {createOrEditEmailTemplatePageUiStore.attachmentTotalSizeValidationMessage}
        </Text>
      }
    </div>
  );
};

export const AttachmentsSection = observer(AttachmentsSectionImpl);
